import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interest: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create plain text message with no special characters or symbols
    const message = `Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}
Interest: ${formData.interest || 'Not specified'}
Message: ${formData.message || 'No additional message'}`;
    
    // WhatsApp number (replace with your actual WhatsApp number)
    const whatsappNumber = '919359663830'; // Without + or special characters
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Show success message
    alert('Thank you! You will be redirected to WhatsApp to send your enquiry.');
    
    // Reset form
    setFormData({ name: '', phone: '', email: '', interest: '', message: '' });
  };

  return (
    <>
      {/* Optional: Add custom styles for animations if needed */}
      <style>{`
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(40px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-slideInRight {
          animation: slideInRight 0.9s ease-out;
        }
        .qr-code-hover:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 25px -5px rgba(20, 184, 166, 0.3);
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" id="contact">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 relative inline-block">
            Get in Touch
            <span className="absolute bottom-0 left-0 w-20 h-1 bg-teal-500 mt-2"></span>
          </h2>
        </div>

        {/* Contact Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 bg-white/90 backdrop-blur-md rounded-3xl lg:rounded-[36px] p-6 md:p-8 lg:p-12 border border-teal-500/30 shadow-lg">
          
          {/* Left Column - Info */}
          <div className="space-y-6 animate-slideInRight">
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 text-left mb-4">Contact Information</h3>
            
            {/* Address */}
            <div className="flex items-start gap-4 text-gray-700 text-left">
              <i className="fas fa-map-marker-alt text-teal-500 text-xl mt-1"></i>
              <span className="text-sm md:text-base text-left">
                SR.NO-3, <br/>KAMAL NAGAR,<br/>NEAR KURKUTE HOSPITAL,<br/>WADGAON SHERI,PUNE
              </span>
            </div>
            
            {/* Phone */}
            <div className="flex items-center gap-4 text-gray-700 text-left">
              <i className="fas fa-phone text-teal-500 text-xl"></i>
              <div className="flex flex-wrap gap-2">
                <a href="tel:9359663830" className="hover:text-amber-500 transition-colors">9359663830</a>
                <span>/</span>
                <a href="tel:8888551919" className="hover:text-amber-500 transition-colors">8888551919</a>
              </div>
            </div>

            {/* Email - Updated with both emails */}
            <div className="flex items-start gap-4 text-gray-700 text-left">
              <i className="fas fa-envelope text-teal-500 text-xl mt-1"></i>
              <div className="flex flex-col gap-1">
                <a href="mailto:rahul@sajenterprises.com" className="hover:text-amber-500 transition-colors break-all text-left">
                  rahul@sajenterprises.com
                </a>
                <a href="mailto:parimal@sajenterprises.com" className="hover:text-amber-500 transition-colors break-all text-left">
                  parimal@sajenterprises.com
                </a>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex items-center gap-4 text-gray-700 text-left">
              <i className="fab fa-whatsapp text-teal-500 text-xl"></i>
              <a href="https://wa.me/919359663830" className="hover:text-amber-500 transition-colors">
                9359663830
              </a>
            </div>

            {/* QR Code Section */}
            <div className="mt-6 flex items-center gap-4 text-left">
              <div className="flex-shrink-0">
                <img 
                  src="/qr.jpeg" 
                  alt="Scan QR Code" 
                  className="w-24 h-24 object-contain qr-code-hover transition-all duration-300 cursor-pointer"
                  onClick={() => window.open('/qr.jpeg', '_blank')}
                />
              </div>
              <div className="text-gray-600 text-left">
                <p className="font-semibold text-teal-600">Scan</p>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-amber-500/40 h-48 md:h-56">
              <iframe
                src="https://www.openstreetmap.org/export/embed.html?bbox=73.912%2C18.555%2C73.962%2C18.585&amp;layer=mapnik&amp;marker=18.570%2C73.937"
                allowFullScreen
                loading="lazy"
                title="map"
                className="w-full h-full border-0"
              ></iframe>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-teal-500/20">
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 text-left mb-6">Quick Enquiry</h3>

            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name *"
                required
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-white/80 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition text-left"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone / WhatsApp *"
                required
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-white/80 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition text-left"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-white/80 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition text-left"
              />
              
              {/* Datalist for interest */}
              <input
                list="serviceList"
                name="interest"
                value={formData.interest}
                onChange={handleChange}
                placeholder="I'm interested in..."
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-white/80 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition text-left"
              />
              <datalist id="serviceList">
                <option value="BATTERIES" />
                <option value="MHE & HPT" />
                <option value="DOCK LEVELERS" />
                <option value="RACKING" />
                <option value="INVERTER" />
                <option value="CHARGER" />
                <option value="SCRAP" />
                <option value="SERVICES" />
              </datalist>

              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Additional details"
                className="w-full px-4 py-3 rounded-2xl border border-gray-300 bg-white/80 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition resize-none text-left"
              ></textarea>

              <button
                type="submit"
                className="w-full bg-amber-500 text-white px-6 py-3 rounded-full font-semibold text-lg shadow-lg shadow-amber-500/30 hover:bg-amber-600 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
              >
                Submit enquiry <i className="fas fa-paper-plane"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;